import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Opens a page's create flow when the mobile bottom-nav "+" deep-links here
 * with `?new=<type>`. The nav navigates to the current route with this param;
 * the page registers the type(s) it handles and gets a callback to open its
 * existing create Modal. The param is stripped afterwards (replace) so a
 * refresh/back does not re-open the drawer.
 *
 * @param {string|string[]} types - the `new=` value(s) this page handles
 *   (e.g. "client", or ["availability", "timeoff"]).
 * @param {(type: string) => void} onOpen - called with the matched type.
 * @param {{ enabled?: boolean }} [options] - when `enabled` is false the
 *   deep-link is ignored (nothing opens, the param is left untouched so a later
 *   flip to enabled can still act). Defaults to enabled.
 */
const useCreateIntent = (types, onOpen, { enabled = true } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!enabled) return;

    const requested = searchParams.get("new");
    if (!requested) return;

    const accepted = Array.isArray(types) ? types : [types];
    if (!accepted.includes(requested)) return;

    onOpen(requested);

    // Copy before mutating: searchParams is owned by React Router. Editing it in
    // place would momentarily change what other readers see this tick.
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    setSearchParams(next, { replace: true });
    // onOpen is intentionally omitted: callers pass an inline function, and the
    // effect is guarded by the one-shot param strip above so it runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams, enabled]);
};

export default useCreateIntent;
